import { z } from "zod";
import {
  APPOINTMENT_BUFFER_MINUTES,
  googlePlaceOrNullSchema,
  isTooSoon,
  offeredEarliestMs,
  requiredLeadMinutes,
  tripWallClockToMs,
  type LeadTimeMode,
  type TripCreateRequest,
} from "@mtform/shared";
import {
  isValidPhone,
  normalizeDateForPayload,
  needsAmPm,
  normalizeTimeForPayload,
  todayIso,
} from "@/lib/format";

// =============================================================================
// Form-side schema (display values, RHF binding) — distinct from the wire
// schema in @mtform/shared. The form holds MM/DD/YYYY date strings, "2:30 PM"
// time strings, and (XXX) XXX-XXXX phones; toTripCreateRequest() converts.
//
// Trip type and transport type intentionally start as empty strings — the
// wizard requires the user to pick rather than presenting a default
// selection. Submitting with an empty value still fails validation, so a
// caller that bypasses the wizard can't slip past with an unselected chip.
// =============================================================================

const additionalLegFormSchema = z.object({
  from: z.string().max(200).default(""),
  to: z.string().max(200).default(""),
  fromCityZip: z.string().max(100).default(""),
  toCityZip: z.string().max(100).default(""),
  fromPlace: googlePlaceOrNullSchema.default(null),
  toPlace: googlePlaceOrNullSchema.default(null),
  time: z.string().default(""),
  notes: z.string().max(500).default(""),
  willCall: z.boolean().default(false),
});

const tripTypeOrEmpty = z.union([
  z.enum(["one_way", "round_trip", "multi_leg"]),
  z.literal(""),
]);

const transportTypeOrEmpty = z.union([
  z.enum(["AMB", "W/C", "BWC", "STR", "BSTR"]),
  z.literal(""),
]);

export const tripFormSchema = z
  .object({
    // ---- Basic info -------------------------------------------------------
    department: z.string().max(100).default(""),
    mrn: z.string().min(1, "MRN is required").max(50),
    costCenter: z
      .string()
      .min(1, "Cost Center is required")
      .max(20),
    firstName: z.string().min(1, "First name is required").max(50),
    lastName: z.string().min(1, "Last name is required").max(50),
    dob: z.string().refine((v) => {
      const iso = normalizeDateForPayload(v);
      return Boolean(iso) && iso <= todayIso();
    }, "Please enter a valid past date (MM/DD/YYYY)"),

    // Order type — standing is disabled for now, so always "one_time".
    orderType: z.literal("one_time").default("one_time"),
    tripType: tripTypeOrEmpty,
    transportType: transportTypeOrEmpty,
    /** Asked only when transportType is W/C or BWC — does dispatch
     *  need to supply a wheelchair or is the patient bringing one?
     *  Empty string for non-wheelchair levels of service. Folded into
     *  the wire payload's pickupNotes ("Wheelchair: patient brings
     *  their own" / "Wheelchair: dispatch to provide") so the driver
     *  sees the answer before pickup. */
    wheelchairSource: z
      .enum(["", "own", "provide"])
      .default(""),

    // ---- Pickup ----------------------------------------------------------
    transportDate: z.string().refine((v) => {
      const iso = normalizeDateForPayload(v);
      return Boolean(iso) && iso >= todayIso();
    }, "Pickup date must be today or later (MM/DD/YYYY)"),
    pickupAddress: z.string().min(1, "Pick-up address is required").max(200),
    // Required only when we DON'T have map coordinates for the address —
    // see the superRefine below. A Google pick or a curated Mount Sinai
    // location gives us exact lat/lon (which we send to dispatch), so
    // the text city/zip is redundant friction there.
    pickupCityZip: z.string().max(100).default(""),
    pickupLocation: z.string().max(200).default(""),
    contactPhone: z
      .string()
      .refine(isValidPhone, "Patient phone must be 10 digits"),

    pickupWillCall: z.boolean().default(false),
    timeType: z.enum(["pickup", "appointment"]).default("pickup"),
    primaryTime: z.string().default(""),
    /** Why a booking inside the minimum lead time should be allowed. Only
     *  collected from - and only accepted for - a caller holding the
     *  `bookShortNotice` permission; the API re-checks both. */
    shortNoticeReason: z.string().max(200).default(""),
    /** Mirror of useHasPermission("bookShortNotice"), seeded by the form.
     *  Lives in form state purely so this schema can tell "you may not book
     *  this soon" apart from "you may, but say why" - zod has no access to
     *  the auth context. Never sent to the API, which checks the real
     *  permission itself rather than believing this flag. */
    canBookShortNotice: z.boolean().default(false),
    pickupNotes: z.string().max(1000).default(""),
    pickupPlace: googlePlaceOrNullSchema.default(null),
    /** How many people are traveling with the patient. "0" is the
     *  common case (patient travels alone); the cap is +3 because
     *  Royal Transportation's fleet doesn't carry larger parties.
     *  Plumbed into the wire payload by prepending a "Companions: X"
     *  line to pickupNotes so triplink dispatch and drivers see it
     *  at a glance without a new contract field. */
    companionCount: z
      .enum(["0", "1", "2", "3"])
      .default("0"),

    // ---- Drop-off --------------------------------------------------------
    destinationAddress: z.string().min(1, "Drop-off address is required").max(200),
    // Required only when there are no drop-off coordinates (see
    // superRefine) — same rationale as pickupCityZip.
    destinationCityZip: z.string().max(100).default(""),
    destinationPlace: googlePlaceOrNullSchema.default(null),
    dropoffNotes: z.string().max(1000).default(""),

    // ---- Round trip ------------------------------------------------------
    returnTimeMode: z.enum(["scheduled", "will_call"]).default("scheduled"),
    returnPickupTime: z.string().default(""),
    returnToOriginalPickup: z.boolean().default(true),
    returnCustomDestinationAddress: z.string().max(200).default(""),
    returnCustomDestinationCityZip: z.string().max(100).default(""),
    returnDestinationPlace: googlePlaceOrNullSchema.default(null),
    returnLegNotes: z.string().max(1000).default(""),

    // ---- Multi-leg -------------------------------------------------------
    additionalLegs: z.array(additionalLegFormSchema).default([]),

    // ---- Requester -------------------------------------------------------
    requestedBy: z.string().min(1, "Requester name is required").max(100),
    callbackPhone: z
      .string()
      .refine(isValidPhone, "Callback phone must be 10 digits"),
    requesterEmail: z
      .string()
      .max(100)
      .refine(
        (v) => v === "" || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/u.test(v),
        "Please enter a valid email (e.g. name@example.com).",
      )
      .default(""),

    // ---- Recurrence ("repeat this trip") ---------------------------------
    // When enabled, the same trip is booked across every date in
    // `repeatDates`. The transport date above acts as the anchor /
    // first occurrence; `repeatWeekdays` + `repeatWeeks` drive the
    // generated list, which the user can prune in the preview.
    // `repeatDates` is the source of truth at submit time.
    repeatEnabled: z.boolean().default(false),
    repeatWeekdays: z.array(z.number().int().min(0).max(6)).default([]),
    repeatWeeks: z.number().int().min(1).max(26).default(4),
    repeatDates: z.array(z.string()).default([]),
    // The pattern `repeatDates` was last built from (repeatPatternKey), so a
    // restored draft keeps the dates the booker took out. Not sent.
    repeatBuiltFrom: z.string().default(""),

    // Dates sent without a clear answer, a single trip's or a series', which
    // may have booked, and the ones the booker has since checked. Not part of
    // the trip: it's kept in the form so it's saved with the draft and
    // survives a reload or a trip to Booked Trips.
    unconfirmed: z
      .object({
        dates: z.array(z.string()),
        checked: z.array(z.string()),
        // What the portal said about a date it refused part of, by date.
        notes: z.record(z.string(), z.string()).default({}),
      })
      .default({ dates: [], checked: [], notes: {} }),
  })
  .superRefine((vals, ctx) => {
    // City/State/ZIP is no longer required. Every address is entered via the
    // Google dropdown (which supplies city/zip + coordinates) or a saved /
    // curated location, and the server backfills coordinates as a safety net,
    // so a missing city/zip never blocks a booking. A gentle, non-blocking
    // hint (AddressPickHint) nudges staff to pick from the dropdown when an
    // address has no coordinates.

    // Recurrence: if turned on, there has to be at least one date to
    // book. (The picker generates these from the weekday + weeks
    // controls; a user could deselect every date in the preview.)
    if (vals.repeatEnabled && vals.repeatDates.length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["repeatDates"],
        message: "Pick at least one date, or turn off Repeat.",
      });
    }
    // Trip type / transport type — starts blank so the wizard makes the
    // user explicitly pick. Surface friendly errors if anything tries to
    // submit with an unselected value.
    if (!vals.tripType) {
      ctx.addIssue({
        code: "custom",
        path: ["tripType"],
        message: "Please pick a trip type",
      });
    }
    if (!vals.transportType) {
      ctx.addIssue({
        code: "custom",
        path: ["transportType"],
        message: "Please pick a level of service",
      });
    }

    // Regular Wheelchair (W/C) needs to know who brings the chair —
    // patient-owned vs dispatch-provided. The follow-up only appears
    // in the wizard when W/C is picked; we validate here so a stale
    // form value or direct API hit can't slip past.
    //
    // Bariatric Wheelchair (BWC) skips this question — Royal can't
    // supply bariatric chairs, so the patient is always bringing
    // their own. The wizard auto-sets wheelchairSource = "own" for
    // BWC, so the field is already filled in by the time we get here.
    if (vals.transportType === "W/C" && !vals.wheelchairSource) {
      ctx.addIssue({
        code: "custom",
        path: ["wheelchairSource"],
        message:
          "Pick whether the patient brings their own wheelchair or dispatch provides one.",
      });
    }

    // A time from 12:00 to 5:59 with no AM or PM is asked (needsAmPm).
    const amPm = "Choose AM or PM.";
    // Pickup time required unless will-call.
    if (!vals.pickupWillCall) {
      const timeHHMM = normalizeTimeForPayload(vals.primaryTime);
      if (needsAmPm(vals.primaryTime)) {
        ctx.addIssue({ code: "custom", path: ["primaryTime"], message: amPm });
      } else if (!timeHHMM) {
        ctx.addIssue({
          code: "custom",
          path: ["primaryTime"],
          message: "Please enter a valid time",
        });
      } else {
        // Dispatch needs warning. This replaces the old "not in the past"
        // check, which let a booker ask for a car two minutes out and then
        // ring up asking where it was. `isTooSoon` also covers the old rule:
        // a time that has already passed is, by definition, too soon.
        //
        // Measured against the ENFORCED floor, so the form only objects when
        // the API would genuinely refuse - never merely because a tidier slot
        // exists. The message quotes `offeredEarliestMs`, which carries a
        // form-filling cushion, so following the suggestion cannot then fail
        // on submit. Future dates fall out naturally (far beyond the floor);
        // Will Call has no time and never reaches here.
        //
        // The browser clock is used here on purpose despite being untrusted:
        // the server re-checks authoritatively, and the cushion absorbs a
        // clock that is a few minutes out. A wildly-wrong clock fails at the
        // API with a clear message rather than booking something impossible.
        const dateIso = normalizeDateForPayload(vals.transportDate);
        const atMs = tripWallClockToMs(dateIso, timeHHMM);
        const mode: LeadTimeMode =
          vals.timeType === "appointment" ? "appointment" : "pickup";
        if (atMs != null && isTooSoon(atMs, Date.now(), mode)) {
          const what = mode === "appointment" ? "appointment" : "pickup";
          if (!vals.canBookShortNotice) {
            const earliest = new Date(offeredEarliestMs(Date.now(), mode));
            const earliestLabel = earliest.toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
              timeZone: "America/New_York",
            });
            ctx.addIssue({
              code: "custom",
              path: ["primaryTime"],
              message: `We need at least ${requiredLeadMinutes(mode)} minutes' notice before the ${what}. The earliest we can take is ${earliestLabel}.`,
            });
          } else if (!vals.shortNoticeReason.trim()) {
            // Permitted to book this soon, but not silently: dispatch is being
            // asked for a favour, so it gets recorded on the audit row.
            ctx.addIssue({
              code: "custom",
              path: ["shortNoticeReason"],
              message: `This ${what} is inside the ${requiredLeadMinutes(mode)}-minute minimum. Add a brief reason to book it anyway.`,
            });
          }
        }
      }
    }

    if (vals.tripType === "round_trip") {
      if (vals.returnTimeMode === "scheduled") {
        const ret = normalizeTimeForPayload(vals.returnPickupTime);
        if (needsAmPm(vals.returnPickupTime)) {
          ctx.addIssue({ code: "custom", path: ["returnPickupTime"], message: amPm });
        } else if (!ret) {
          ctx.addIssue({
            code: "custom",
            path: ["returnPickupTime"],
            message: "Please enter a return pickup time",
          });
        } else if (!vals.pickupWillCall) {
          const primary = normalizeTimeForPayload(vals.primaryTime);
          if (primary && ret <= primary) {
            ctx.addIssue({
              code: "custom",
              path: ["returnPickupTime"],
              message: "Return pickup time must be after the initial time",
            });
          }
        }
      }
      if (!vals.returnToOriginalPickup) {
        if (!vals.returnCustomDestinationAddress.trim()) {
          ctx.addIssue({
            code: "custom",
            path: ["returnCustomDestinationAddress"],
            message: "Return destination address is required",
          });
        }
      }
    }

    if (vals.tripType === "multi_leg") {
      if (vals.additionalLegs.length === 0) {
        ctx.addIssue({
          code: "custom",
          path: ["additionalLegs"],
          message: "Add at least one additional leg",
        });
      }
      vals.additionalLegs.forEach((leg, i) => {
        if (!leg.from.trim()) {
          ctx.addIssue({
            code: "custom",
            path: ["additionalLegs", i, "from"],
            message: "From address is required",
          });
        }
        if (!leg.to.trim()) {
          ctx.addIssue({
            code: "custom",
            path: ["additionalLegs", i, "to"],
            message: "To address is required",
          });
        }
        if (!leg.willCall && needsAmPm(leg.time)) {
          ctx.addIssue({ code: "custom", path: ["additionalLegs", i, "time"], message: amPm });
        } else if (!leg.willCall && !normalizeTimeForPayload(leg.time)) {
          ctx.addIssue({
            code: "custom",
            path: ["additionalLegs", i, "time"],
            message: "Time is required",
          });
        }
      });
    }
  });

export type TripFormValues = z.input<typeof tripFormSchema>;
export type TripFormParsed = z.output<typeof tripFormSchema>;

export const tripFormDefaults: TripFormValues = {
  department: "",
  mrn: "",
  costCenter: "",
  firstName: "",
  lastName: "",
  dob: "",
  orderType: "one_time",
  tripType: "",
  transportType: "",
  wheelchairSource: "",
  transportDate: "",
  pickupAddress: "",
  pickupCityZip: "",
  pickupLocation: "",
  contactPhone: "",
  pickupWillCall: false,
  timeType: "pickup",
  primaryTime: "",
  shortNoticeReason: "",
  // Overwritten from useHasPermission once the form mounts.
  canBookShortNotice: false,
  pickupNotes: "",
  pickupPlace: null,
  companionCount: "0",
  destinationAddress: "",
  destinationCityZip: "",
  destinationPlace: null,
  dropoffNotes: "",
  returnTimeMode: "scheduled",
  returnPickupTime: "",
  returnToOriginalPickup: true,
  returnCustomDestinationAddress: "",
  returnCustomDestinationCityZip: "",
  returnDestinationPlace: null,
  returnLegNotes: "",
  additionalLegs: [],
  requestedBy: "",
  callbackPhone: "",
  requesterEmail: "",
  repeatEnabled: false,
  repeatWeekdays: [],
  repeatWeeks: 4,
  repeatDates: [],
  repeatBuiltFrom: "",
  unconfirmed: { dates: [], checked: [], notes: {} },
};

// =============================================================================
// Form → wire conversion. Mirrors the static script.js submit handler so the
// triplink.ai integration is unchanged.
// =============================================================================

export function toTripCreateRequest(values: TripFormParsed): TripCreateRequest {
  // The schema's superRefine already errored if these were empty, so by the
  // time we reach this helper the wizard has guaranteed real selections.
  const tripType = values.tripType as Exclude<typeof values.tripType, "">;
  const transportType = values.transportType as Exclude<typeof values.transportType, "">;

  const isWillCall = values.pickupWillCall;
  const primaryTimeNorm = normalizeTimeForPayload(values.primaryTime);
  const isAppointment = !isWillCall && values.timeType === "appointment";

  const wire: TripCreateRequest = {
    mrn: values.mrn,
    costCenter: values.costCenter,
    department: values.department,
    firstName: values.firstName,
    lastName: values.lastName,
    dob: normalizeDateForPayload(values.dob),

    orderType: values.orderType,
    tripType,
    transportType,

    transportDate: normalizeDateForPayload(values.transportDate),
    pickupAddress: values.pickupAddress,
    pickupCityZip: values.pickupCityZip,
    pickupLocation: values.pickupLocation,
    contactPhone: values.contactPhone,

    timeType: isWillCall ? "will_call" : values.timeType,
    pickupTimeEstimated: isWillCall
      ? "WILL_CALL"
      : isAppointment
        ? ""
        : primaryTimeNorm,
    appointmentTime: isWillCall ? "" : isAppointment ? primaryTimeNorm : "",
    pickupNotes: foldDispatchNotes(values),
    pickupPlace: values.pickupPlace ?? null,

    destinationAddress: values.destinationAddress,
    destinationCityZip: values.destinationCityZip,
    destinationPlace: values.destinationPlace ?? null,
    dropoffNotes: values.dropoffNotes,

    returnTimeMode: tripType === "round_trip" ? values.returnTimeMode : "",
    returnPickupTime:
      tripType !== "round_trip"
        ? ""
        : values.returnTimeMode === "will_call"
          ? "WILL_CALL"
          : normalizeTimeForPayload(values.returnPickupTime),
    returnToOriginalPickup:
      tripType !== "round_trip" ? true : values.returnToOriginalPickup,
    returnCustomDestinationAddress:
      tripType === "round_trip" && !values.returnToOriginalPickup
        ? values.returnCustomDestinationAddress
        : "",
    returnCustomDestinationCityZip:
      tripType === "round_trip" && !values.returnToOriginalPickup
        ? values.returnCustomDestinationCityZip
        : "",
    returnDestinationPlace:
      tripType === "round_trip" && !values.returnToOriginalPickup
        ? values.returnDestinationPlace ?? null
        : null,
    returnLegNotes: tripType === "round_trip" ? values.returnLegNotes : "",

    additionalLegs:
      tripType === "multi_leg"
        ? values.additionalLegs.map((leg) => ({
            from: leg.from,
            to: leg.to,
            fromCityZip: leg.fromCityZip,
            toCityZip: leg.toCityZip,
            fromPlace: leg.fromPlace ?? null,
            toPlace: leg.toPlace ?? null,
            time: leg.willCall ? "WILL_CALL" : normalizeTimeForPayload(leg.time),
            notes: leg.notes,
            willCall: leg.willCall,
          }))
        : [],

    standingDefaultPickupTime: "",
    standingDefaultAppointmentTime: "",
    standingSchedule: [],

    requestedBy: values.requestedBy,
    callbackPhone: values.callbackPhone,
    requesterEmail: values.requesterEmail,

    createdAt: new Date().toISOString(),
    source: "mount-sinai-trip-request-form",
  };

  // 15-min buffer convention — when the patient picks an Appointment Time,
  // they should be on campus 15 minutes before. We surface the derived
  // arrival in the UI ("on campus by 9:45 AM") and pass the buffer in the
  // payload so dispatch can correlate.
  if (isAppointment) {
    // Shared constant, not a literal: this same number decides how much extra
    // notice an appointment-mode booking needs (leadTime.ts) and the pickup
    // time PickupSection shows. All three have to move together.
    wire.appointmentBufferMinutes = APPOINTMENT_BUFFER_MINUTES;
  }

  // Only sent when actually filled. The API ignores it unless the caller
  // really holds `bookShortNotice` and the booking really is inside the
  // minimum, so a stray value can't buy anything.
  const shortNotice = values.shortNoticeReason?.trim();
  if (shortNotice) {
    wire.shortNoticeReason = shortNotice;
  }

  return wire;
}

/** Prepend dispatch-relevant flags ("Companions: N traveling with
 *  patient", "Wheelchair: ...") to the user-typed pickupNotes string.
 *
 *  Threading these through the existing notes field (instead of a new
 *  wire-schema field) is deliberate: it keeps the contract with
 *  triplink unchanged AND makes the info show up in the very first
 *  line drivers see on their dispatch screen.
 *
 *  Segments are joined with " · " (space-middot-space) rather than "\n"
 *  because triplink's dispatch UI collapses newlines into spaces — with
 *  middots the structure survives the flatten and stays scannable. */
export function foldDispatchNotes(values: TripFormParsed): string {
  const lines: string[] = [];
  if (values.companionCount && values.companionCount !== "0") {
    lines.push(`Companions: ${values.companionCount} traveling with patient`);
  }
  if (values.transportType === "W/C" || values.transportType === "BWC") {
    if (values.wheelchairSource === "own") {
      lines.push("Wheelchair: patient brings their own");
    } else if (values.wheelchairSource === "provide") {
      lines.push("Wheelchair: dispatch provides");
    }
  }
  const trimmedNotes = (values.pickupNotes ?? "").trim();
  if (trimmedNotes) lines.push(trimmedNotes);
  return lines.join(" · ");
}
